import { getApiBaseUrl } from '@/shared/lib/app-config'

export const API_BASE = getApiBaseUrl()
export const REFRESH_TOKEN_KEY = 'auth_refresh_token'
export const SESSION_EXPIRES_AT_KEY = 'auth_session_expires_at'
export const LEGACY_AUTH_TOKEN_KEY = 'auth_token'
export const AUTH_SESSION_TTL_MS = 55 * 60 * 1000

interface RefreshTokenResponse {
  refreshToken: string
}

type AuthSessionClearedListener = () => void

export class AuthRefreshError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'AuthRefreshError'
  }
}

let refreshPromise: Promise<void> | null = null
const authSessionClearedListeners = new Set<AuthSessionClearedListener>()

function notifyAuthSessionCleared() {
  authSessionClearedListeners.forEach((listener) => listener())
}

export function subscribeToAuthSessionCleared(listener: AuthSessionClearedListener) {
  authSessionClearedListeners.add(listener)
  return () => authSessionClearedListeners.delete(listener)
}

function hasRefreshTokenResponse(value: unknown): value is RefreshTokenResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'refreshToken' in value &&
    typeof value.refreshToken === 'string'
  )
}

function getSessionExpiresAt(): number | null {
  const expiresAt = localStorage.getItem(SESSION_EXPIRES_AT_KEY)
  if (!expiresAt) return null

  const parsed = Number(expiresAt)
  return Number.isFinite(parsed) ? parsed : null
}

export function hasRefreshToken(): boolean {
  return !!localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function isAuthSessionFresh(now = Date.now()): boolean {
  const expiresAt = getSessionExpiresAt()
  return !!expiresAt && expiresAt > now
}

export function setStoredAuthSession(refreshToken: string | null) {
  const hadStoredSession =
    !!localStorage.getItem(REFRESH_TOKEN_KEY) ||
    !!localStorage.getItem(SESSION_EXPIRES_AT_KEY) ||
    !!localStorage.getItem(LEGACY_AUTH_TOKEN_KEY)

  localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY)

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
    localStorage.setItem(SESSION_EXPIRES_AT_KEY, String(Date.now() + AUTH_SESSION_TTL_MS))
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(SESSION_EXPIRES_AT_KEY)
    if (hadStoredSession) {
      notifyAuthSessionCleared()
    }
  }
}

export function clearStoredAuthSession() {
  setStoredAuthSession(null)
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text()
  if (text.trim() === '') {
    return undefined as T
  }

  return JSON.parse(text) as T
}

async function refreshAuthSessionRequest(): Promise<void> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
  if (!refreshToken) {
    throw new AuthRefreshError(401, 'Missing refresh token')
  }

  const response = await fetch(`${API_BASE}/v2/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ refreshToken }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    const message =
      typeof error === 'object' &&
      error !== null &&
      'error' in error &&
      typeof error.error === 'string'
        ? error.error
        : `HTTP ${response.status}`
    clearStoredAuthSession()
    throw new AuthRefreshError(response.status, message)
  }

  const data = await parseJsonResponse<unknown>(response)
  if (!hasRefreshTokenResponse(data)) {
    clearStoredAuthSession()
    throw new AuthRefreshError(500, 'Refresh response did not include a refresh token')
  }

  setStoredAuthSession(data.refreshToken)
}

export async function refreshAuthSession(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = refreshAuthSessionRequest().finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}
